import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, merge, Observable, of } from "rxjs";
import {
    GetStorageItemListRequest,
    GetStorageItemListResponse,
    GetStorageItemResponse,
    GetStorageItemMovementListResponse,
    GetStorageItemMovementListRequest,
    StorageClient,
    Quantity,
    GetStorageItemBinListResponse,
    GetStorageItemBinListRequest,
    GetStorageTankListResponse,
    GetStorageTankListRequest,
    GetStorageTankFilesResponse,
    FileResponse,
    AccumulatedQuantity,
} from "../WebApiClient";
import {
    distinctUntilChanged,
    map,
    publishReplay,
    refCount,
    switchMap,
} from "rxjs/operators";
import { SortEvent } from "../models/ngSortable";
import { ToastService } from './toast.service';

@Injectable()
export class StorageService {
    private trigger = new BehaviorSubject(true);

    public constructor(
        private readonly storageClient: StorageClient,
        private readonly toastService: ToastService
    ) { }

    public getStorageItemsObservable(
        pageObservable: Observable<{
            page: number;
            keyword: string;
            sort: SortEvent;
            hideZeroQuantity: number;
        }>
    ): Observable<GetStorageItemListResponse | undefined> {
        let maxResultsPerPage = 25;
        let o = combineLatest(
            pageObservable.pipe(distinctUntilChanged()),
            this.trigger
        ).pipe(
            map((x) => ({ shouldResolve: x[1], request: x[0] })),
            switchMap((x) =>
                x.shouldResolve
                    ? merge(
                          of(undefined),
                          this.storageClient.getStorageItemList(
                              new GetStorageItemListRequest({
                                  page: x.request.page,
                                  keyword: x.request.keyword,
                                  sortColumn: x.request.sort.column,
                                  sortDirection: x.request.sort.direction,
                                  hideZeroQuantity: x.request.hideZeroQuantity,
                              })
                          )
                      )
                    : of(undefined)
            ),
            publishReplay(2),
            refCount()
        );

        return o;
    }

    public getStorageItemObservable(
        id: string
    ): Observable<GetStorageItemResponse | undefined> {
        return this.storageClient.getStorageItem(id);
    }

    public getStorageItemMovementsObservable(
        pageObservable: Observable<{
            id: string;
            page: number;
            sort: SortEvent;
        }>
    ): Observable<GetStorageItemMovementListResponse | undefined> {
        let maxResultsPerPage = 25;
        let o = combineLatest(
            pageObservable.pipe(distinctUntilChanged()),
            this.trigger
        ).pipe(
            map((x) => ({ shouldResolve: x[1], request: x[0] })),
            switchMap((x) =>
                x.shouldResolve && x.request.id
                    ? merge(
                          of(undefined),
                          this.storageClient.getStorageItemMovementList(
                              new GetStorageItemMovementListRequest({
                                  page: x.request.page,
                                  sortColumn: x.request.sort.column,
                                  sortDirection: x.request.sort.direction,
                                  secondarySortColumn: x.request.sort.secondaryColumn,
                                  secondarySortDirection: x.request.sort.secondaryDirection,
                              }),
                              x.request.id
                          )
                      )
                    : of(undefined)
            ),
            publishReplay(2),
            refCount()
        );

        return o;
    }

    public getStorageItemMovementQuantityObservable(
        id: string,
        year: number
    ): Observable<Quantity[] | undefined> {
        return this.storageClient
            .getStorageItemMovementQuantityList(id, year)
            .pipe(map((x) => x.quantityList));
    }

    public getStorageAccumulatedQuantityObservable(
        id: string,
        year: number
    ): Observable<AccumulatedQuantity[]|undefined>{
        return this.storageClient
            .getStorageItemAccumulatedQuantityList(id, year)
            .pipe(map((x) => x.accumulatedQuantityList));
    }

    public getStorageItemBinsObservable(
        pageObservable: Observable<{
            id: string;
            page: number;
            sort: SortEvent;
        }>
    ): Observable<GetStorageItemBinListResponse | undefined> {
        let maxResultsPerPage = 25;
        let o = combineLatest(
            pageObservable.pipe(distinctUntilChanged()),
            this.trigger
        ).pipe(
            map((x) => ({ shouldResolve: x[1], request: x[0] })),
            switchMap((x) =>
                x.shouldResolve && x.request.id
                    ? merge(
                          of(undefined),
                          this.storageClient.getStorageItemBinList(
                              new GetStorageItemBinListRequest({
                                  page: x.request.page,
                                  sortColumn: x.request.sort.column,
                                  sortDirection: x.request.sort.direction,
                              }),
                              x.request.id
                          )
                      )
                    : of(undefined)
            ),
            publishReplay(2),
            refCount()
        );

        return o;
    }

    public getStorageTanksObservable(
        pageObservable: Observable<{
            id: string;
            page: number;
            sort: SortEvent;
        }>
    ): Observable<GetStorageTankListResponse | undefined> {
        let maxResultsPerPage = 25;
        let o = combineLatest(
            pageObservable.pipe(distinctUntilChanged()),
            this.trigger
        ).pipe(
            map((x) => ({ shouldResolve: x[1], request: x[0] })),
            switchMap((x) =>
                x.shouldResolve && x.request.id
                    ? merge(
                          of(undefined),
                          this.storageClient.getStorageTankList(
                              new GetStorageTankListRequest({
                                  page: x.request.page,
                                  sortColumn: x.request.sort.column,
                                  sortDirection: x.request.sort.direction,
                              }),
                              x.request.id
                          )
                      )
                    : of(undefined)
            ),
            publishReplay(2),
            refCount()
        );

        return o;
    }

    public getStorageTankFilesObectObservable(
        itemId: string,
        tankId: string
    ): Observable<GetStorageTankFilesResponse | undefined> {
        return this.storageClient.getStorageTankFiles(itemId, tankId);
    }

    public async downloadFile(
        itemId: string,
        tankId: string,
        fileName: string
    ) {
        let pdf = await this.storageClient
            .getStorageTankFile(itemId, tankId, fileName)
            .toPromise();

        this.download2(pdf);
    }

    public async exportStorageItems(
        keyword: string,
        withMovements: boolean
    ) {
        try {
            let csv = await this.storageClient
                .exportStorageItems(keyword, withMovements)
                .toPromise();

            this.download2(csv);
        } catch (error: any) {
            if (error.status === 404) {
                this.toastService.add(
                    "No items found",
                    "There are no storage items matching your filters.",
                    "warning"
                );
            } else {
                this.toastService.add(
                    "Export failed",
                    "An unexpected error occurred while exporting items.",
                    "danger"
                );
            }
        }
    }

    private download2(file: FileResponse) {
        if (file.fileName == null || file.fileName == undefined) {
            return;
        }

        let url = window.URL.createObjectURL(file.data);
        let a = document.createElement("a");

        a.href = url;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
    }
}
